import type { Subscription } from '@fff-web/growth-sdk';

export type CoreBoxState = 'ACTIVE' | 'CANCELED' | 'EXPIRED';

/**
 * The box that *is* the membership (annual/seasonal), as opposed to add-on boxes like beauty/pup.
 * Mirrors asado's `isCoreBoxSub`.
 */
const isCoreBoxSub = (sub: Subscription): boolean =>
  sub.type === 'BOX' &&
  (sub.customizeSaleType == null || sub.customizeSaleType === 'seasonal_customize_box');

/**
 * The trial record in growth's v2 shape. Both conjuncts matter: v1 carries `trialEndAt` on the BOX
 * record, so `!!trialEndAt` alone would match a v1 trial too. Mirrors asado's `isTrialSub`.
 */
export const isTrialSub = (sub: Subscription): boolean =>
  sub.type === 'MEMBERSHIP' && !!sub.trialEndAt;

/**
 * The member's membership status as shown on every surface (navbars + My Account): the core box's
 * state, not `membership_state` — an active refill/SWB keeps the latter 'active' after the box is
 * cancelled or lapses. Mirrors asado's `getBoxSubscriptionState` (the BFF `rawSubscriptionState`):
 *   - running v2 trial → its own `nonrenew` (an active core box next to it wins)
 *   - lapsed v2 trial with no active core box → EXPIRED
 *   - active core box with `nonrenew` → CANCELED (in-term, one-click reactivate)
 *   - active core box otherwise → ACTIVE
 *   - only lapsed core boxes on record → EXPIRED
 *   - no core box or trial (club, add-on only) → undefined, so callers fall back to their own source
 * Takes every growth subscription, unfiltered — a pre-filtered list can drop the core box when an
 * add-on box is ACTIVE while the core box is NON_RENEWING.
 */
export const getCoreBoxState = (subscriptions: Subscription[] = []): CoreBoxState | undefined => {
  const trialSubs = subscriptions.filter(isTrialSub);
  const coreBoxSubs = subscriptions.filter(isCoreBoxSub);
  const activeTrial = trialSubs.find((sub) => sub.active);
  const activeCoreBox = coreBoxSubs.find((sub) => sub.active);

  if (activeTrial) return (activeCoreBox ?? activeTrial).nonrenew ? 'CANCELED' : 'ACTIVE';
  if (trialSubs.length > 0 && !activeCoreBox) return 'EXPIRED';
  if (activeCoreBox) return activeCoreBox.nonrenew ? 'CANCELED' : 'ACTIVE';
  if (coreBoxSubs.length > 0) return 'EXPIRED';
  return undefined;
};
